import type { EdgelessRootService, EmbedCardStyle } from '@blocksuite/blocks';
import {
  createLitPortal,
  DarkLoadingIcon,
  EmbedCardDarkBannerIcon,
  EmbedCardDarkCubeIcon,
  EmbedCardDarkHorizontalIcon,
  EmbedCardDarkListIcon,
  EmbedCardDarkVerticalIcon,
  EmbedCardLightBannerIcon,
  EmbedCardLightCubeIcon,
  EmbedCardLightHorizontalIcon,
  EmbedCardLightListIcon,
  EmbedCardLightVerticalIcon,
  EmbedYoutubeBlockComponent,
  LightLoadingIcon,
  ThemeObserver,
  youtubeUrlRegex,
} from '@blocksuite/blocks';
import { debounce } from '@blocksuite/global/utils';
import { flip, offset } from '@floating-ui/dom';
import { getPiqueCloudBaseUrl } from '@pique/core/modules/cloud/services/fetch';
import { addTauriAuthHeaders } from '@pique/core/modules/cloud/utils/tauri-auth';
import { getBaseUrl } from '@pique/graphql';
import { type CSSResultGroup, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  pauseMediaSlot,
  seekVideoSlot,
  videoCompletedSlot,
} from '../pique-slots';
// Minimal local definition to replace removed audio Transcriber types
type TranscriberData = {
  isBusy: boolean;
  text: string;
  chunks: Array<{ text: string; timestamp: [number | null, number | null] }>;
};
import type { PiqueEmbedYoutubeModel } from './youtube-embed-block';

declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string,
        options: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: Record<string, (event: YTPlayerEvent) => void>;
        }
      ) => YTPlayer;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayerEvent {
  data: number;
  target: YTPlayer;
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getDuration: () => number;
  getCurrentTime: () => number;
  getVolume: () => number;
  setVolume: (volume: number) => void;
  isMuted: () => boolean;
  mute: () => void;
  unMute: () => void;
  getAvailablePlaybackRates: () => number[];
  getPlaybackRate: () => number;
  setPlaybackRate: (rate: number) => void;
  cueVideoById: (options: { videoId: string; startSeconds: number }) => void;
  destroy: () => void;
}

const pastelColors = [
  'var(--affine-palette-line-magenta)',
  'var(--affine-palette-line-purple)',
  'var(--affine-brand-color)',
];

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

export const EMBED_CARD_WIDTH: Record<EmbedCardStyle, number> = {
  horizontal: 752,
  horizontalThin: 752,
  list: 752,
  vertical: 364,
  cube: 170,
  cubeThick: 170,
  video: 752,
  figma: 752,
  html: 752,
  syncedDoc: 752,
};

export const EMBED_CARD_HEIGHT: Record<EmbedCardStyle, number> = {
  horizontal: 116,
  horizontalThin: 80,
  list: 46,
  vertical: 390,
  cube: 114,
  cubeThick: 132,
  video: 544,
  figma: 544,
  html: 544,
  syncedDoc: 455,
};

interface EmbedCardIcons {
  LoadingIcon: typeof LightLoadingIcon;
  EmbedCardBannerIcon: typeof EmbedCardLightBannerIcon;
  EmbedCardHorizontalIcon: typeof EmbedCardLightHorizontalIcon;
  EmbedCardListIcon: typeof EmbedCardLightListIcon;
  EmbedCardVerticalIcon: typeof EmbedCardLightVerticalIcon;
  EmbedCardCubeIcon: typeof EmbedCardLightCubeIcon;
}

export function getEmbedCardIcons(): EmbedCardIcons {
  const theme = ThemeObserver.mode;
  if (theme === 'light') {
    return {
      LoadingIcon: LightLoadingIcon,
      EmbedCardBannerIcon: EmbedCardLightBannerIcon,
      EmbedCardHorizontalIcon: EmbedCardLightHorizontalIcon,
      EmbedCardListIcon: EmbedCardLightListIcon,
      EmbedCardVerticalIcon: EmbedCardLightVerticalIcon,
      EmbedCardCubeIcon: EmbedCardLightCubeIcon,
    };
  } else {
    return {
      LoadingIcon: DarkLoadingIcon,
      EmbedCardBannerIcon: EmbedCardDarkBannerIcon,
      EmbedCardHorizontalIcon: EmbedCardDarkHorizontalIcon,
      EmbedCardListIcon: EmbedCardDarkListIcon,
      EmbedCardVerticalIcon: EmbedCardDarkVerticalIcon,
      EmbedCardCubeIcon: EmbedCardDarkCubeIcon,
    };
  }
}

/**
 * Custom YouTube embed block component that integrates with the transcription widget.
 */
@customElement('pique-youtube-embed-block')
// @ts-expect-error - Static side type mismatch with lit styles property
export class PiqueYoutubeEmbedBlockComponent extends EmbedYoutubeBlockComponent {
  override get model(): PiqueEmbedYoutubeModel {
    return super.model as PiqueEmbedYoutubeModel;
  }

  static override styles: CSSResultGroup = [
    EmbedYoutubeBlockComponent.styles,
    css`
      .affine-embed-youtube-block {
        position: relative;
        z-index: 1000; /* High z-index to ensure it's above other elements */
        pointer-events: auto !important; /* Force pointer events */
        padding: 0;
        border-radius: 10px;
        border: 1px solid var(--affine-border-color);
        display: flex;
        flex-direction: column;
        height: auto;
        overflow: hidden;
      }

      /* Add styles for a click mask */
      .youtube-click-mask {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 999; /* Just below controls but above other page elements */
        pointer-events: auto !important;
      }

      /* Ensure video container is above the mask */
      .affine-embed-youtube-video-iframe-container {
        position: relative;
        z-index: 1000;
        pointer-events: auto !important;
      }

      /* Ensure controls are above the mask */
      .custom-controls {
        position: relative;
        z-index: 1001;
        pointer-events: auto !important;
      }

      .affine-embed-youtube-content {
        display: none;
      }

      .affine-embed-youtube-video {
        margin: 0;
        flex-grow: 1;
      }

      .affine-embed-youtube-video-iframe-container {
        position: relative;
        width: 100%;
        padding-top: 56.25%;
        overflow: hidden;
      }

      .affine-embed-youtube-video-iframe-container iframe,
      .affine-embed-youtube-video-iframe-container .ytplayer {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      /* Custom controls styling */
      .custom-controls {
        display: flex;
        flex-direction: column;
        padding: 8px;
        position: relative;
      }

      .controls-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .controls-left {
        display: flex;
        align-items: center;
      }

      .controls-right {
        display: flex;
        align-items: center;
      }

      .control-button + .control-button {
        margin-left: 8px;
      }

      .progress-bar-container {
        position: relative;
        height: 2px;
        background-color: var(--affine-text-secondary-color);
        cursor: pointer;
        margin-bottom: 8px;
        border-radius: 2px;
        /* Slower transition with ease timing */
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .progress-bar-container:hover {
        height: 4px;
      }

      .progress-bar {
        position: absolute;
        height: 100%;
        background-color: #ff0000;
        width: 0%;
        border-radius: 2px;
      }

      .control-button {
        background: none;
        border: none;
        color: var(--affine-text-primary-color);
        cursor: pointer;
        padding: 0 4px;
        display: flex;
        align-items: center;
      }

      .control-button svg {
        fill: var(--affine-text-primary-color);
        width: 20px;
        height: 20px;
      }

      .time-display {
        color: var(--affine-text-primary-color);
        font-size: 11px;
        margin-left: 4px;
      }

      .volume-control {
        display: flex;
        align-items: center;
        margin-left: 4px;
      }

      .volume-slider {
        width: 60px;
        margin-left: 2px;
      }

      .bookmark {
        position: absolute;
        top: -3px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        cursor: pointer;
        transform: translateX(-50%);
        /* Match the container's transition */
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .progress-bar-container:hover .bookmark {
        top: -4px;
      }

      /* Overlay to capture clicks */
      .video-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1000;
        pointer-events: auto !important;
      }

      .video-container .ytplayer {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      /* Overlay to capture clicks */
      .video-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
        background: transparent;
        z-index: 1;
        pointer-events: auto !important;
      }

      /* Playback rate select styling */
      .playback-rate-select {
        background: var(--affine-background-primary-color);
        color: var(--affine-text-primary-color);
        border: 1px solid var(--affine-border-color);
        padding: 2px 4px;
        border-radius: 4px;
        font-size: 11px;
        margin-left: 4px;
      }
    `,
  ];

  override accessor useCaptionEditor = false;

  @property({ type: Number })
  accessor currentPlaybackTime = 0;

  @state()
  accessor transcriptionData: TranscriberData | undefined;

  private player: YTPlayer | null = null;
  private playerReady = false;
  private timeUpdateInterval: ReturnType<typeof setInterval> | null = null;

  private isYouTubeAPIReady = false;

  private iframeReplaced = false;

  private isPlaying = false;
  private duration = 0;
  private volume = 100;

  private abortController: AbortController | null = null;

  @state()
  accessor playbackRates: number[] = [];

  @state()
  accessor currentPlaybackRate: number = 1;


  private readonly saveLastPlayingTime = debounce((time: number) => {
    if (this.isPlaying) {
      this.doc.updateBlock(this.model, {
        lastPlayingTime: time,
      });
    }
  }, 300);

  // Override refreshData to implement custom logic
  override refreshData = async () => {
    try {
      this.loading = true;

      const url = this.model.url;

      const urlMatch = url.match(youtubeUrlRegex);
      if (urlMatch) {
        const [, videoId] = urlMatch;
        this.doc.updateBlock(this.model, {
          videoId,
        });
      } else {
        this.doc.updateBlock(this.model, {
          videoId: null,
        });
        return;
      }

      try {
        const oEmbedUrl = `https://youtube.com/oembed?url=${encodeURIComponent(
          url
        )}&format=json`;
        const response = await fetch(oEmbedUrl);
        if (response.ok) {
          const data = await response.json();
          const {
            title,
            author_name: creator,
            author_url: creatorUrl,
            thumbnail_url: image,
          } = data;
          this.doc.updateBlock(this.model, {
            title,
            creator,
            creatorUrl,
            image,
          });

          // Safely handle edgelessRootService operations
          try {
            const edgelessRootService = this.host.std.spec.getService(
              'affine:page'
            ) as unknown as EdgelessRootService;

            // Only proceed if edgelessRootService exists and has the required method
            if (
              edgelessRootService?.currentDocTitle &&
              edgelessRootService?.setDocTitleFromString
            ) {
              const currentTitle = edgelessRootService.currentDocTitle();
              // don't update title if it's already set
              if (!currentTitle || currentTitle.length === 0) {
                edgelessRootService.setDocTitleFromString(title);
              }
            }
          } catch (error) {
            console.warn('Failed to update document title:', error);
            // Continue execution as this is not a critical error
          }
        }
      } catch (error) {
        console.error('Error fetching YouTube data:', error);
      }
    } catch (error) {
      console.error('Error in refreshData:', error);
    } finally {
      this.loading = false;
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();

    // Load YouTube IFrame Player API
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
      // Set up the onYouTubeIframeAPIReady function
      window.onYouTubeIframeAPIReady = () => {
        this.isYouTubeAPIReady = true;
        this.onYouTubeIframeAPIReady();
      };
    } else if (window.YT && window.YT.Player) {
      // API already loaded
      this.isYouTubeAPIReady = true;
      this.onYouTubeIframeAPIReady();
    } else {
      // API script is already included but not yet ready
      window.onYouTubeIframeAPIReady = () => {
        this.isYouTubeAPIReady = true;
        this.onYouTubeIframeAPIReady();
      };
    }

    // Listen for the seek time via the slot
    this._disposables.add(
      seekVideoSlot.on(time => {
        this.seekToTime(time);
      })
    );

    this._disposables.add(
      pauseMediaSlot.on(() => {
        this.player?.pauseVideo();
      })
    );

    // Listen for model updates
    this._disposables.add(
      this.model.propsUpdated.on(({ key }) => {
        if (key === 'url') {
          this.handleUrlChange();
        }
      })
    );

    // Listen for block added updates
    this._disposables.add(
      this.host.doc.slots.blockUpdated.on(({ type, id }) => {
        if (type === 'add') {
          this.handleBlockAddedToPage(id);
        }
      })
    );

    // Listen for block removed updates
    this._disposables.add(
      this.host.doc.slots.blockUpdated.on(({ type, id }) => {
        if (type === 'delete') {
          this.handleBlockRemovedFromPage(id);
        }
      })
    );

    // Initialize data
    this.refreshData().catch(error => {
      console.error('Error in refreshData:', error);
    });

    // Check if blockTimestamps is empty, and if so, create initial block
    if (
      !this.model.blockTimestamps ||
      Object.keys(this.model.blockTimestamps).length === 0
    ) {
      this.createInitialBlock();
    }
  }

  private createInitialBlock() {
    const noteId = this.doc.getBlocksByFlavour('affine:note')[0]?.id;
    if (!noteId) return;
    setTimeout(() => {
      const paragraphId = this.doc.addBlock(
        'affine:paragraph',
        {
          text: new this.doc.Text(),
        },
        noteId
      );

      // Generate a color for this blockId
      const colorIndex = simpleHash(paragraphId) % pastelColors.length;
      const color = pastelColors[colorIndex];

      // Update the model with the new block timestamp and color
      this.doc.updateBlock(this.model, {
        blockTimestamps: {
          ...this.model.blockTimestamps,
          [paragraphId]: { timestamp: 0, color },
        },
      });
    }, 100);
  }

  private handleUrlChange() {
    // Reset transcription data
    const emptyTranscriptionData: TranscriberData = {
      isBusy: true,
      text: '',
      chunks: [],
    };
    this.transcriptionData = emptyTranscriptionData;
    // Note: Transcription reset now handled by centralized service

    // Reset the iframeReplaced flag
    this.iframeReplaced = false;

    // Reinitialize the player
    if (this.isYouTubeAPIReady) {
      this.initializePlayer();
    }

    // Fetch new data
    this.refreshData().catch(error => {
      console.error('Error in refreshData:', error);
    });
  }

  private readonly onYouTubeIframeAPIReady = () => {
    this.initializePlayer();
  };

  private initializePlayer() {
    const videoId = this.model.videoId;
    if (!videoId) return;

    if (!window.YT || !window.YT.Player) {
      return;
    }

    if (this.player) {
      this.player.destroy();
      this.player = null;
      this.playerReady = false;
    }

    this.player = new window.YT.Player(`ytplayer-${this.blockId}`, {
      videoId: videoId,
      playerVars: {
        playsinline: 1,
        controls: 0,
        autoplay: 0,
      },
      events: {
        onReady: this.onPlayerReady,
        onStateChange: this.onPlayerStateChange.bind(this),
        onPlaybackRateChange: this.onPlaybackRateChangeEvent.bind(this),
      },
    });
  }

  private readonly onPlayerReady = (_event: YTPlayerEvent) => {
    this.playerReady = true;

    if (!this.player) return;

    this.duration = this.player.getDuration();
    this.volume = this.player.getVolume();

    // Get available playback rates
    this.playbackRates = this.player.getAvailablePlaybackRates();
    this.currentPlaybackRate = this.player.getPlaybackRate();

    // Set up time update interval
    this.timeUpdateInterval = setInterval(this.handleVideoTimeUpdate, 500); // Adjust interval as needed

    // Fetch captions
    const videoId = this.model.videoId;
    if (videoId) {
      this.fetchYouTubeSubtitles(videoId)
        .then(data => {
          this.transcriptionData = data;
          // Note: YouTube transcription integration with central service will be added later
        })
        .catch(error => {
          console.error('Error fetching YouTube subtitles:', error);
          this.transcriptionData = undefined;
        });
    }

    // Seek to the last saved time if available, but do not start playing
    if (this.model.lastPlayingTime > 0 && this.player && this.model.videoId) {
      this.player.cueVideoById({
        videoId: this.model.videoId,
        startSeconds: this.model.lastPlayingTime,
      });
    }
  };

  private readonly onPlayerStateChange = (event: YTPlayerEvent) => {
    const state = event.data;
    if (window.YT && state === window.YT.PlayerState.PLAYING) {
      this.isPlaying = true;
    } else {
      this.isPlaying = false;
    }
    this.requestUpdate();
  };

  private handleBlockAddedToPage(blockId: string) {
    const timeAtBlockCreation = this.currentPlaybackTime || 0;

    // Check if blockId is already in blockTimestamps
    if (this.model.blockTimestamps && this.model.blockTimestamps[blockId]) {
      return;
    }

    // Check if timestamp is already in blockTimestamps, don't add duplicate timestamps
    if (
      this.model.blockTimestamps &&
      Object.values(this.model.blockTimestamps).some(
        t => t.timestamp === timeAtBlockCreation
      )
    ) {
      return;
    }

    // Generate a color for this blockId
    const colorIndex = simpleHash(blockId) % pastelColors.length;
    const color = pastelColors[colorIndex];

    // Update the model with the new block timestamp and color
    this.doc.updateBlock(this.model, {
      blockTimestamps: {
        ...this.model.blockTimestamps,
        [blockId]: { timestamp: timeAtBlockCreation, color },
      },
    });
  }

  private handleBlockRemovedFromPage(blockId: string) {
    // remove block id from model's block timestamps
    const newBlockTimestamps = { ...this.model.blockTimestamps };
    delete newBlockTimestamps[blockId];
    this.doc.updateBlock(this.model, {
      blockTimestamps: newBlockTimestamps,
    });
  }

  private readonly handleVideoTimeUpdate = () => {
    if (this.player && this.playerReady) {
      const currentTime = this.player.getCurrentTime();
      const duration = this.player.getDuration();
      const percentageWatched = (currentTime / duration) * 100;

      // Check for video completion
      if (percentageWatched >= 95) {
        videoCompletedSlot.emit(true);
      }

      this.currentPlaybackTime = currentTime;
      this.saveLastPlayingTime(currentTime);
      this.requestUpdate('currentPlaybackTime');
    }
  };

  private seekToTime(time: number) {
    if (this.player && this.playerReady) {
      this.player.seekTo(time, true);
    }
  }

  // Transcript fetching
  private async fetchYouTubeSubtitles(
    videoId: string,
    retryCount = 0
  ): Promise<TranscriberData> {
    const maxRetries = 3;
    const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 3000); // exponential backoff, max 3 seconds

    try {
      const baseUrl = environment.isTauri ? getPiqueCloudBaseUrl() : getBaseUrl();
      const url = new URL(
        baseUrl + `/api/copilot/youtube-transcript/${videoId}`
      );
      const headers = await addTauriAuthHeaders({});
      const response = await fetch(url.toString(), { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch subtitles ' + response.statusText);
      }
      const data = await response.json();

      // Loading complete

      return data;
    } catch (error) {
      console.error('Error fetching subtitles:', error);

      // Loading failed

      // Only retry if we haven't exceeded max retries and it's a potentially temporary error
      if (
        retryCount < maxRetries &&
        error instanceof Error &&
        (error.message.includes('Failed to fetch') ||
          error.message.includes('timeout') ||
          error.message.includes('network') ||
          error.message.includes('429')) // Too Many Requests
      ) {
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        return this.fetchYouTubeSubtitles(videoId, retryCount + 1);
      }

      throw error;
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    // Clean up the player
    if (this.player && this.player.destroy) {
      this.player.destroy();
    }
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }

    // Save the current playback time one last time
    if (this.currentPlaybackTime !== undefined) {
      this.saveLastPlayingTime(this.currentPlaybackTime);
    }
  }

  private onPlaybackRateChangeEvent(event: YTPlayerEvent) {
    this.currentPlaybackRate = event.data;
    this.requestUpdate();
  }

  private onPlaybackRateChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const rate = parseFloat(select.value);
    if (this.playerReady && this.player) {
      this.player.setPlaybackRate(rate);
      this.currentPlaybackRate = rate;
    }
  }

  override renderBlock() {
    const {
      image,
      videoId,
      style,
      title,
      creator,
    } = this.model;

    this._cardStyle = style;
    this._width = EMBED_CARD_WIDTH[this._cardStyle];
    this._height = EMBED_CARD_HEIGHT[this._cardStyle];

    const loading = this.loading;
    const { EmbedCardBannerIcon } = getEmbedCardIcons();
    const bannerImage =
      !loading && image
        ? html`<object type="image/webp" data=${image} draggable="false">
            ${EmbedCardBannerIcon}
          </object>`
        : EmbedCardBannerIcon;

    // Determine if the player is muted
    const isMuted =
      this.playerReady &&
      this.player &&
      typeof this.player.isMuted === 'function' &&
      this.player.isMuted();

    const isSelected = !!this.selected?.is('block') || !!this.selected?.is('surface');
    return this.renderEmbed(
      () => html`
        <div @click=${(e: MouseEvent) => e.stopPropagation()}>
          <div
            class=${classMap({
        'affine-embed-youtube-block': true,
        loading,
        selected: isSelected,
      })}
          >
            <!-- Click mask to prevent event propagation -->
            <div
              class="youtube-click-mask"
              @click=${(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
            ></div>

            <!-- Moved Content to the top -->
            <div class="affine-embed-youtube-content">
              <div class="affine-embed-youtube-content-header">
                <div class="affine-embed-youtube-content-title-text">
                  ${title ?? ''}
                </div>
                <div class="affine-embed-youtube-content-creator-text">
                  ${creator ?? ''}
                </div>
              </div>
            </div>

            <!-- Video Section -->
            <div class="affine-embed-youtube-video">
              ${videoId
          ? html`
                    <div class="affine-embed-youtube-video-iframe-container">
                      <div class="video-container">
                        <div
                          id="ytplayer-${this.blockId}"
                          class="ytplayer"
                        ></div>
                        <!-- Overlay to capture clicks -->
                        <div
                          class="video-overlay"
                          @click=${(e: MouseEvent) => this.onVideoClick(e)}
                        ></div>
                      </div>
                    </div>
                  `
          : bannerImage}
            </div>

            <!-- Custom Controls -->
            <div class="custom-controls">
              <div
                class="progress-bar-container"
                @click=${this.onProgressBarClick}
                @mousemove=${this.onProgressBarMouseMove}
                @mouseleave=${this.onProgressBarMouseLeave}
              >
                <div
                  class="progress-bar"
                  style="width: ${(this.currentPlaybackTime / this.duration) *
        100}%"
                ></div>
                ${this.renderBookmarks()}
              </div>
              <div class="controls-row">
                <div class="controls-left">
                  <button
                    class="control-button"
                    @click=${this.togglePlayPause}
                    aria-label=${this.isPlaying ? 'Pause' : 'Play'}
                  >
                    ${this.isPlaying
          ? this.renderPauseIcon()
          : this.renderPlayIcon()}
                  </button>
                  <div class="volume-control">
                    <button
                      class="control-button"
                      @click=${this.toggleMute}
                      aria-label=${isMuted ? 'Unmute' : 'Mute'}
                    >
                      ${this.renderVolumeIcon()}
                    </button>
                    <input
                      type="range"
                      class="volume-slider"
                      min="0"
                      max="100"
                      .value=${this.volume}
                      @input=${this.onVolumeChange}
                    />
                  </div>
                  <span class="time-display">
                    ${this.formatTime(this.currentPlaybackTime)} /
                    ${this.formatTime(this.duration)}
                  </span>
                </div>
                <div class="controls-right">
                  <select
                    class="playback-rate-select"
                    @change=${this.onPlaybackRateChange}
                  >
                    ${this.playbackRates.map(
            rate =>
              html`<option
                          value=${rate}
                          ?selected=${rate === this.currentPlaybackRate}
                        >
                          ${rate}x
                        </option>`
          )}
                  </select>
                  <button
                    class="control-button"
                    @click=${this.toggleFullScreen}
                    aria-label="Fullscreen"
                  >
                    ${this.renderFullScreenIcon()}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `
    );
  }

  private renderBookmarks() {
    if (!this.model.blockTimestamps || this.duration === 0) return nothing;

    return html`
      ${Object.values(this.model.blockTimestamps).map(
      ({ timestamp, color }) => {
        const positionPercent = (timestamp / this.duration) * 100;
        return html`
            <div
              class="bookmark"
              style="left: ${positionPercent}%; background-color: ${color};"
              @click=${(e: Event) => {
            e.stopPropagation();
            this.seekToTime(timestamp);
          }}
            ></div>
          `;
      }
    )}
    `;
  }

  private onProgressBarClick(event: MouseEvent) {
    if (!this.playerReady || !this.player) return;

    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;
    const percent = clickX / width;
    const newTime = percent * this.duration;
    this.seekToTime(newTime);
  }

  private onProgressBarMouseMove(event: MouseEvent) {
    if (!this.playerReady || !this.player || !this.duration) return;

    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const width = rect.width;
    const percent = mouseX / width;
    const timeAtHover = percent * this.duration;

    const timeString = this.formatTime(timeAtHover);

    // Fetch the transcription text corresponding to the hovered time
    const transcriptionText = this.getTranscriptionAtTime(timeAtHover);

    // Use createLitPortal to show the popup
    const progressBarContainer = progressBar;

    // Abort previous popup if any
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    createLitPortal({
      template: html`
        <div
          style="
            background: rgba(0, 0, 0, 0.85);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 12px 16px;
            border-radius: 8px;
            color: white;
            font-size: 13px;
            line-height: 1.5;
            z-index: 1000;
            max-width: 500px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          "
        >
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;"
              >${timeString}</span
            >
            <span style="color: rgba(255, 255, 255, 0.8); white-space: normal;"
              >${transcriptionText || 'No transcription available.'}</span
            >
          </div>
        </div>
      `,
      container: progressBarContainer,
      computePosition: {
        referenceElement: progressBarContainer,
        placement: 'top',
        strategy: 'fixed',
        middleware: [
          offset({
            mainAxis: 10,
            crossAxis: 0,
          }),
          flip({
            padding: 5,
            fallbackPlacements: ['bottom'],
          }),
        ],
        autoUpdate: true,
      },
      abortController: this.abortController,
      closeOnClickAway: true,
    });
  }

  private onProgressBarMouseLeave() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private getTranscriptionAtTime(timeInSeconds: number): string | null {
    const transcriptionData: TranscriberData | undefined =
      this.transcriptionData;

    if (!transcriptionData || !transcriptionData.chunks) {
      return null;
    }

    // Find the chunk that includes the given time
    const chunk = transcriptionData.chunks.find(
      chunk =>
        chunk.timestamp[0] !== null &&
        chunk.timestamp[1] !== null &&
        timeInSeconds >= chunk.timestamp[0] &&
        timeInSeconds <= chunk.timestamp[1]
    );

    return chunk ? chunk.text : null;
  }

  private togglePlayPause() {
    if (!this.playerReady || !this.player) return;

    if (this.isPlaying) {
      this.player.pauseVideo();
    } else {
      this.player.playVideo();
    }
  }

  private toggleMute() {
    if (!this.playerReady || !this.player) return;

    if (this.player.isMuted()) {
      this.player.unMute();
      this.volume = this.player.getVolume();
    } else {
      this.player.mute();
    }
    this.requestUpdate();
  }

  private onVolumeChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const volume = parseInt(input.value, 10);
    this.volume = volume;
    if (this.player && this.playerReady) {
      this.player.setVolume(volume);
      if (volume === 0) {
        this.player.mute();
      } else {
        this.player.unMute();
      }
    }
  }

  private toggleFullScreen() {
    const iframeContainer = this.renderRoot.querySelector(
      '.affine-embed-youtube-video-iframe-container'
    ) as HTMLElement & {
      webkitRequestFullScreen?: () => Promise<void>;
      mozRequestFullScreen?: () => Promise<void>;
    };
    if (iframeContainer.requestFullscreen) {
      iframeContainer.requestFullscreen();
    } else if (iframeContainer.webkitRequestFullScreen) {
      iframeContainer.webkitRequestFullScreen();
    } else if (iframeContainer.mozRequestFullScreen) {
      iframeContainer.mozRequestFullScreen();
    }
  }

  private formatTime(time: number): string {
    const totalSeconds = Math.floor(time);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds - hours * 3600) / 60);
    const seconds = totalSeconds - hours * 3600 - minutes * 60;
    return `${hours > 0 ? hours + ':' : ''}${hours > 0 && minutes < 10 ? '0' : ''
      }${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  private renderPlayIcon() {
    return html`<svg viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z"></path>
    </svg>`;
  }

  private renderPauseIcon() {
    return html`<svg viewBox="0 0 24 24">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path>
    </svg>`;
  }

  private renderVolumeIcon() {
    if (!this.playerReady || !this.player) return nothing;

    const volume = this.player.getVolume();
    const isMuted = this.player.isMuted();

    if (isMuted || volume === 0) {
      // Muted icon
      return html`<svg viewBox="0 0 24 24">
        <path
          d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.06c1.48-.73 2.5-2.26 2.5-4.03zM3 9v6h4l5 5V4L7 9H3z"
        ></path>
        <path
          d="M14 3.23v17.54c4.97-1.65 8.34-6.25 8.34-11.77s-3.37-10.12-8.34-11.77z"
          fill="none"
        ></path>
      </svg>`;
    } else if (volume > 50) {
      // Volume high icon
      return html`<svg viewBox="0 0 24 24">
        <path d="M3 9v6h4l5 5V4L7 9H3z"></path>
        <path
          d="M16.5 12c0-2.76-2.24-5-5-5v2a3 3 0 0 1 3 3 3 3 0 0 1-3 3v2c2.76 0 5-2.24 5-5z"
        ></path>
      </svg>`;
    } else {
      // Volume low icon
      return html`<svg viewBox="0 0 24 24">
        <path d="M3 9v6h4l5 5V4L7 9H3z"></path>
        <path
          d="M10 12c0-1.77-1.02-3.29-2.5-4.03v8.06C8.98 15.29 10 13.77 10 12z"
        ></path>
      </svg>`;
    }
  }

  private renderFullScreenIcon() {
    return html`<svg viewBox="0 0 24 24">
      <path
        d="M7 14h2v3h3v2H7v-5zm0-9h5v2H9v3H7V5zm10 9h-2v3h-3v2h5v-5zm0-9v5h-2V7h-3V5h5z"
      ></path>
    </svg>`;
  }

  private onVideoClick(event: MouseEvent) {
    // Stop event propagation
    event.preventDefault();
    event.stopPropagation();

    if (this.playerReady && this.player) {
      this.togglePlayPause();
    }
  }

  override updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties);
    if (!this.iframeReplaced) {
      this.replaceIframeWithDiv();
    }
  }

  private replaceIframeWithDiv() {
    if (this.iframeReplaced) return; // Already replaced

    const videoId = this.model.videoId;
    if (!videoId) return;

    const iframeContainer = this.renderRoot.querySelector(
      '.affine-embed-youtube-video-iframe-container'
    );
    if (!iframeContainer) return;

    const iframe = iframeContainer.querySelector('iframe');
    if (iframe) {
      // Remove the iframe
      iframe.remove();
    }

    // Create a new div for the player if it doesn't exist
    let playerDiv = iframeContainer.querySelector(
      `.ytplayer-${this.blockId}`
    ) as HTMLElement;

    if (!playerDiv) {
      playerDiv = document.createElement('div');
      playerDiv.className = `ytplayer-${this.blockId} ytplayer`;
      iframeContainer.append(playerDiv);

      // Initialize the player
      if (this.isYouTubeAPIReady) {
        this.initializePlayer();
      }
    }

    // Set the flag to indicate the iframe has been replaced
    this.iframeReplaced = true;
  }
}
